import type { PortalThread } from '@central/api';
import {
  Card,
  PageHeader,
  StatusChip,
  color,
  font,
  formatAgo,
  formatDateTime,
  status,
} from '@central/ui';
import { Badge, Button, TextInput } from '@mantine/core';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useRouteContext, useSearch } from '@tanstack/react-router';
import { useEffect, useState, type CSSProperties } from 'react';
import { api } from '../api';
import { QueryState } from '../components/QueryState';
import { SupportedBy } from '../components/SupportedBy';
import { activeStatuses, statusLabel, threadChip } from '../components/threadLabels';
import type { MessagesListSearch } from './messagesSearch';

type Show = 'open' | 'resolved';

/** The statuses each Conversations filter lists. */
const showStatus: Record<Show, string> = {
  open: activeStatuses,
  resolved: 'closed',
};

/** Messages & reports (docs/design/CustomerMessages.dc.html): every conversation, full width. */
export function Messages() {
  const search = useSearch({ from: '/app/messages' });
  const { me } = useRouteContext({ from: '/app' });
  const show = search.show ?? 'open';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        title="Messages & reports"
        subtitle="Questions, renewals, bugs, ideas and crashes"
        actions={
          <Button h={40} renderRoot={(props) => <Link {...props} to="/messages/new" />}>
            New message
          </Button>
        }
      />
      <SupportedBy reseller={me.customer.reseller} />
      <CrashBanner />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 12,
          flexWrap: 'wrap',
          borderBottom: `1px solid ${color.border}`,
        }}
      >
        <nav aria-label="Filter conversations" style={{ display: 'flex', gap: 4 }}>
          <ShowLink show="open" on={!search.q && show === 'open'} label="Open" />
          <ShowLink show="resolved" on={!search.q && show === 'resolved'} label="Resolved" />
        </nav>
        <ConversationSearch q={search.q} />
      </div>
      <Conversations show={show} q={search.q} listSearch={{ show: search.show, q: search.q }} />
    </div>
  );
}

/** A crash this recent shows the banner; replies to its thread do not count. */
const CRASH_BANNER_MS = 24 * 60 * 60 * 1000;

/**
 * The crash-detected banner: the most recent automatic crash report of the
 * last day that is not resolved, with a link to its thread. A crash a
 * customer reported by hand is not one.
 */
function CrashBanner() {
  const crashes = useQuery({
    queryKey: ['threads', 'list', 'crash-banner'],
    queryFn: () =>
      api.threads.list({
        kind: 'crash',
        status: activeStatuses,
        limit: 10,
      }),
  });
  const now = Date.now();
  let t: (PortalThread & { lastCrashAt: string }) | undefined;
  for (const x of crashes.data?.items ?? []) {
    const at = x.lastCrashAt;
    if (x.startedBy !== 'system' || !at || now - Date.parse(at) >= CRASH_BANNER_MS) continue;
    if (!t || Date.parse(at) > Date.parse(t.lastCrashAt)) t = { ...x, lastCrashAt: at };
  }
  if (!t) return null;
  return (
    <div
      role="status"
      aria-label="Crash detected"
      style={{
        display: 'flex',
        gap: 14,
        alignItems: 'center',
        flexWrap: 'wrap',
        background: status.down.bg,
        color: color.crashBannerText,
        borderRadius: 10,
        padding: '12px 16px',
        fontSize: 14,
        lineHeight: '20px',
        marginBottom: 16,
      }}
    >
      <span style={{ flex: '1 1 300px' }}>
        <b>Crash detected:</b> {t.subject} at {formatDateTime(t.lastCrashAt)} UTC. A report was sent
        to Rethink automatically.
      </span>
      <Link
        to="/messages/$threadId"
        params={{ threadId: t.id }}
        style={{ color: color.crashBannerText, fontWeight: 600 }}
      >
        View report
      </Link>
    </div>
  );
}

const columns = '96px minmax(260px, 1fr) 160px 170px 96px';

const cell: CSSProperties = { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' };

function Conversations({
  show,
  q,
  listSearch,
}: {
  show: Show;
  q?: string;
  listSearch: MessagesListSearch;
}) {
  const list = useInfiniteQuery({
    queryKey: ['threads', 'list', q ? { q } : show],
    queryFn: ({ pageParam }) =>
      api.threads.list(
        q ? { q, cursor: pageParam } : { status: showStatus[show], cursor: pageParam },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
  });
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <Card padded={false}>
      {!list.data ? (
        <div style={{ padding: '16px 20px' }}>
          <QueryState error={list.error} />
        </div>
      ) : items.length === 0 ? (
        <p style={{ margin: 0, padding: '16px 20px', fontSize: 14, color: color.textMuted }}>
          {q
            ? `No conversations match “${q}”.`
            : show === 'open'
              ? 'No open conversations. Send Rethink a message and it shows up here.'
              : 'No resolved conversations yet.'}
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 720 }}>
            <div
              aria-hidden
              style={{
                display: 'grid',
                gridTemplateColumns: columns,
                gap: 16,
                padding: '10px 20px',
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: color.textMuted,
              }}
            >
              <span>Ref</span>
              <span>Conversation</span>
              <span>Deployment</span>
              <span>Status</span>
              <span>Last activity</span>
            </div>
            <nav aria-label="Conversations">
              {items.map((t) => (
                <ThreadRow key={t.id} t={t} listSearch={listSearch} />
              ))}
            </nav>
          </div>
        </div>
      )}
      {list.hasNextPage && (
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${color.rowRule}` }}>
          <Button
            variant="default"
            h={40}
            loading={list.isFetchingNextPage}
            onClick={() => void list.fetchNextPage()}
          >
            Load more
          </Button>
        </div>
      )}
    </Card>
  );
}

/** Finds a conversation by its ticket reference, subject or message text, open or resolved. */
function ConversationSearch({ q }: { q?: string }) {
  const navigate = useNavigate({ from: '/messages' });
  const [draft, setDraft] = useState(q ?? '');
  useEffect(() => setDraft(q ?? ''), [q]);
  const go = (next: string) =>
    void navigate({ search: (s) => ({ show: s.show, q: next || undefined }) });
  return (
    <form
      role="search"
      style={{
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        paddingBottom: 8,
        flex: '0 1 420px',
        minWidth: 0,
      }}
      onSubmit={(e) => {
        e.preventDefault();
        go(draft.trim());
      }}
    >
      <TextInput
        aria-label="Search conversations"
        placeholder="GC-1042, subject or message text"
        style={{ flex: 1, minWidth: 0 }}
        styles={{ input: { minHeight: 40 } }}
        maxLength={100}
        value={draft}
        onChange={(e) => setDraft(e.currentTarget.value)}
      />
      <Button type="submit" h={40} variant="default">
        Search
      </Button>
      {q && (
        <Button h={40} variant="subtle" onClick={() => go('')}>
          Clear
        </Button>
      )}
    </form>
  );
}

/** A Conversations filter tab with its count; the count is left off until it loads. */
function ShowLink({ show, on, label }: { show: Show; on: boolean; label: string }) {
  const count = useQuery({
    queryKey: ['threads', 'count', show],
    queryFn: () => api.threads.count({ status: showStatus[show] }),
  });
  return (
    <Link
      to="/messages"
      search={{ show: show === 'open' ? undefined : show }}
      aria-current={on ? 'page' : undefined}
      style={{
        minHeight: 40,
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0 12px',
        marginBottom: -1,
        fontSize: 14,
        fontWeight: on ? 600 : 500,
        textDecoration: 'none',
        color: on ? color.portalNavActiveText : color.textBody,
        borderBottom: `2px solid ${on ? color.portalMark : 'transparent'}`,
      }}
    >
      {label}
      {count.data && ` · ${count.data.threads}`}
    </Link>
  );
}

/** The latest post's author and opening words; nothing before the first reply is read. */
function previewLine(t: PortalThread): string | undefined {
  const p = t.preview;
  if (!p) return undefined;
  const who =
    p.authorKind === 'staff'
      ? 'Rethink'
      : p.authorKind === 'system'
        ? 'Report'
        : (p.authorName ?? 'You');
  return `${who}: “${p.excerpt}”`;
}

function ThreadRow({ t, listSearch }: { t: PortalThread; listSearch: MessagesListSearch }) {
  const chip = threadChip(t);
  const preview = previewLine(t);
  return (
    <Link
      to="/messages/$threadId"
      params={{ threadId: t.id }}
      search={listSearch}
      style={{
        display: 'grid',
        gridTemplateColumns: columns,
        gap: 16,
        alignItems: 'center',
        minHeight: 56,
        padding: '12px 20px',
        borderTop: `1px solid ${color.rowRule}`,
        textDecoration: 'none',
        color: color.text,
        fontSize: 14,
      }}
    >
      <span style={{ ...cell, fontSize: 13, fontFamily: font.mono, color: color.textMuted }}>
        {t.ref}
      </span>
      <span style={{ ...cell, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 }}>
          <StatusChip tone={chip.tone}>{chip.label}</StatusChip>
          <span
            style={{
              ...cell,
              whiteSpace: 'nowrap',
              fontWeight: t.unread > 0 ? 700 : 500,
            }}
          >
            {t.subject}
          </span>
          {t.unread > 0 && (
            <Badge size="sm" style={{ flex: 'none' }} aria-label={`${t.unread} unread`}>
              {t.unread}
            </Badge>
          )}
        </span>
        {preview && (
          <span style={{ ...cell, fontSize: 13, color: color.textMuted, whiteSpace: 'nowrap' }}>
            {preview}
          </span>
        )}
        {t.match && (
          <span
            aria-label="Matching message"
            style={{
              fontSize: 13,
              fontStyle: 'italic',
              color: color.textBody,
              borderLeft: `2px solid ${color.rowRule}`,
              paddingLeft: 8,
              overflowWrap: 'anywhere',
            }}
          >
            {t.match}
          </span>
        )}
      </span>
      <span style={{ ...cell, fontSize: 13, color: color.textBody, whiteSpace: 'nowrap' }}>
        {t.environment?.name}
      </span>
      <span style={{ ...cell, fontSize: 13, color: color.textBody }}>{statusLabel[t.status]}</span>
      <span style={{ fontSize: 13, color: color.textMuted }}>{formatAgo(t.lastMessageAt)}</span>
    </Link>
  );
}
